import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-lg cursor-pointer"
      >
        <div className="aspect-[16/9] w-full overflow-hidden rounded-lg">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 国产传媒`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-lg"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-block px-2.5 py-1 text-xs bg-primary-500 text-white rounded-full mb-3 whitespace-nowrap">
            {article.categoryName}
          </span>
          <h3 className="text-white text-xl md:text-2xl font-heading font-bold mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-white/70 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-white/50 text-xs">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-24 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 国产传媒`}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span>·</span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="aspect-[16/10] w-full overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={`${article.title} - 国产传媒`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-5">
        <span className="inline-block px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full mb-3 whitespace-nowrap">
          {article.categoryName}
        </span>
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center gap-3 text-xs text-foreground-400">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.publishDate}</span>
          <span>·</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}